import { defineMessages, useIntl } from '@faceup/localization'
import { UploadDragAndDrop, type UploadDragAndDropProps, ucImagePlus } from '@faceup/ui'
import { Alert, Upload, useMessage } from '@faceup/ui-base'
import {
  DEFAULT_LANGUAGE,
  FORM_ITEMS,
  FormItemType,
  getSupportUrlByLanguage as getSupportUrlByLanguageUtil,
  hasFileValidExtension,
  type Language,
  removeFileMetadata,
} from '@faceup/utils'
import { useCallback, useState } from 'react'

const messages = defineMessages('abstractForm.fileFormElement', [
  { id: 'fileUpload', defaultMessage: 'Click here or drag the file to upload' },
  {
    id: 'fileUploadHint',
    defaultMessage: 'Upload up to {maxCount} files (maximum {maxSizePerFile} MB each)',
    values: {
      maxCount: 'Max upload files count',
      maxSizePerFile: 'Max size per file (number in Megabytes)',
    },
  },
  { id: 'fileUploadError', defaultMessage: "The file couldn't be uploaded." },
  {
    id: 'anonymityAlert',
    defaultMessage:
      'Attachments can break your anonymity, so we always try to remove all metadata from attachments.',
  },
  { id: 'anonymityAlertLearnMore', defaultMessage: 'Learn more' },
  {
    id: 'attachmentsTooLargeFile',
    defaultMessage:
      'The file you are trying to upload is too large. Maximum file size is {maxSizePerFile} MB.',
    values: {
      maxSizePerFile: 'Max size per file (number in Megabytes)',
    },
  },
  {
    id: 'unsupportedMimetype',
    defaultMessage:
      'We do not support this file type. Please, upload a file in JPG, PNG, PDF, DOC, DOCX, ODT, ODS, XLS, XLSX, TXT, MOV, MP3, MP4, AVI or WAV.',
  },
  { id: 'metadataRemoved', defaultMessage: 'Metadata have been removed' },
  { id: 'metadataUnableToRemove', defaultMessage: 'Metadata have not been removed' },
] as const)

const bytesToMegabytes = (bytes: number) => Math.round(bytes / (1024 * 1024))

type FileFormElementProps = Omit<UploadDragAndDropProps, 'beforeUpload'> & {
  maxCount: number
  isPreview?: boolean
  anonymityInfo?: {
    enabled: boolean
    language?: Language
  }
}

/**
 * TMP placement for this component so this package has no dependencies on report package
 * Will change.
 */

export const FileFormElement = (props: FileFormElementProps) => {
  const { accept, content, customRequest, ...restProps } = props
  const maxSizePerFile = FORM_ITEMS[FormItemType.File].maxSize
  const message = useMessage()
  const [showAnonymityAlert, setShowAnonymityAlert] = useState<boolean>(false)
  const { formatMessage } = useIntl()

  const getSupportUrlByLanguage = useCallback((language: Language) => {
    return getSupportUrlByLanguageUtil(language)
  }, [])

  return (
    <>
      <UploadDragAndDrop
        {...restProps}
        accept={accept ?? Object.keys(FORM_ITEMS[FormItemType.File].allowedMimeTypes).join(',')}
        content={
          content ?? {
            icon: ucImagePlus,
            text: formatMessage(messages.fileUpload),
            hint: formatMessage(messages.fileUploadHint, {
              values: {
                maxCount: props.maxCount,
                maxSizePerFile: bytesToMegabytes(maxSizePerFile),
              },
            }),
            isPreview: props.isPreview,
          }
        }
        onChange={params => {
          setShowAnonymityAlert(true)
          props.onChange?.(params)
        }}
        customRequest={
          customRequest
            ? customRequest
            : ({ file, onSuccess }) => {
                if (file instanceof File) {
                  onSuccess?.(null)
                } else {
                  message.error({
                    content: formatMessage(messages.fileUploadError),
                  })
                }
              }
        }
        beforeUpload={async (file, fileList) => {
          if (file.size > maxSizePerFile || fileList.some(f => f.size > maxSizePerFile)) {
            message.error(
              formatMessage(messages.attachmentsTooLargeFile, {
                values: {
                  maxSizePerFile: bytesToMegabytes(maxSizePerFile),
                },
              })
            )
            return Upload.LIST_IGNORE
          }

          if (
            !Object.keys(FORM_ITEMS[FormItemType.File].allowedMimeTypes).includes(file.type) ||
            !hasFileValidExtension(file.name, file.type)
          ) {
            message.error({
              content: formatMessage(messages.unsupportedMimetype),
            })
            return Upload.LIST_IGNORE
          }

          const { file: processedFile, processed } = await removeFileMetadata(file)
          if (processed) {
            message.success(formatMessage(messages.metadataRemoved))
          } else {
            message.error(formatMessage(messages.metadataUnableToRemove))
          }
          return processedFile
        }}
      />
      {props.anonymityInfo?.enabled && showAnonymityAlert && (
        <Alert
          className='mt-4'
          type='info'
          closable
          message={
            <div style={{ padding: 5 }}>
              {formatMessage(messages.anonymityAlert)}{' '}
              <a
                className='underline'
                href={getSupportUrlByLanguage(props.anonymityInfo.language ?? DEFAULT_LANGUAGE)}
                target='_blank'
                rel='noopener noreferrer'
              >
                {formatMessage(messages.anonymityAlertLearnMore)}
              </a>
            </div>
          }
        />
      )}
    </>
  )
}
